import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangleIcon, PlusIcon, XIcon } from 'lucide-react';
import type { Localized, VocabPair } from '@org/model';
import { Alert, AlertDescription, Button, Input, Label, MediaUploader, TextArea, cn } from '@org/ui';

// Small building blocks shared by the woman editor sections and the card forms.

export const IMAGE_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const VIDEO_FILE_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

type SegmentedOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
  title?: string;
};

/** Two or three choices shown as buttons side by side (Upload | Link, EN | PT…). */
export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('inline-flex shrink-0 items-center gap-1 rounded-md bg-muted p-1', className)}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            disabled={option.disabled}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-sm px-3 py-1 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A labelled form row with an optional hint under it. */
export function FormField({
  label,
  hint,
  htmlFor,
  children,
  className,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** Two columns on wide screens (English | Portuguese), one on narrow ones. */
export function FieldPair({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}>{children}</div>;
}

const emptyLocalized: Localized = { en: '', pt: '' };

/** One text in English and Portuguese, side by side. */
export function LocalizedField({
  idBase,
  label,
  hint,
  value,
  onChange,
  multiline = false,
  rows = 4,
}: {
  idBase: string;
  label: string;
  hint?: React.ReactNode;
  value: Localized | undefined;
  onChange: (value: Localized) => void;
  multiline?: boolean;
  rows?: number;
}) {
  const { t } = useTranslation('admin');
  const current = value ?? emptyLocalized;
  const renderInput = (lang: 'en' | 'pt') => {
    const id = `${idBase}-${lang}`;
    const langLabel = lang === 'en' ? t('women.editor.langEn') : t('women.editor.langPt');
    return (
      <FormField key={lang} htmlFor={id} label={`${label} (${langLabel})`}>
        {multiline ? (
          <TextArea
            id={id}
            rows={rows}
            value={current[lang]}
            onChange={(event) => onChange({ ...current, [lang]: event.target.value })}
          />
        ) : (
          <Input
            id={id}
            value={current[lang]}
            onChange={(event) => onChange({ ...current, [lang]: event.target.value })}
          />
        )}
      </FormField>
    );
  };
  return (
    <div className="flex flex-col gap-1.5">
      <FieldPair>
        {renderInput('en')}
        {renderInput('pt')}
      </FieldPair>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export type MediaSourceMode = 'upload' | 'reference';

/**
 * A picture or video given either by uploading a file (default) or by pasting
 * a link. A pasted link can show a warning that it may disappear at the source.
 */
export function MediaSourceField({
  idBase,
  kind,
  mode,
  onModeChange,
  url,
  onUrlChange,
  linkPlaceholder,
  showReferenceWarning = false,
}: {
  idBase: string;
  kind: 'image' | 'video';
  mode: MediaSourceMode;
  onModeChange: (mode: MediaSourceMode) => void;
  url: string;
  onUrlChange: (url: string) => void;
  linkPlaceholder?: string;
  showReferenceWarning?: boolean;
}) {
  const { t } = useTranslation('admin');
  const hasUrl = url.trim().length > 0;

  return (
    <div className="flex flex-col gap-2">
      <SegmentedToggle<MediaSourceMode>
        className="self-start"
        value={mode}
        onChange={onModeChange}
        options={[
          { value: 'upload', label: t('women.media.upload') },
          { value: 'reference', label: t('women.media.link') },
        ]}
      />

      {mode === 'upload' ? (
        hasUrl ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
            <span className="shrink-0">{t('women.media.current')}</span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-blue-700 underline"
            >
              {url}
            </a>
            <Button type="button" size="sm" variant="outline" onClick={() => onUrlChange('')}>
              {t('women.media.replace')}
            </Button>
          </div>
        ) : (
          <MediaUploader
            compact
            acceptedFileTypes={kind === 'image' ? IMAGE_FILE_TYPES : VIDEO_FILE_TYPES}
            onUploadComplete={(uploadedUrl) => onUrlChange(uploadedUrl)}
            onError={(error) => toast.error(t('women.media.uploadError', { message: error.message }))}
          />
        )
      ) : (
        <>
          <Input
            id={`${idBase}-link`}
            type="url"
            inputMode="url"
            value={url}
            placeholder={linkPlaceholder ?? t('women.media.linkPlaceholder')}
            onChange={(event) => onUrlChange(event.target.value)}
          />
          {showReferenceWarning ? (
            <Alert variant="warning" className="py-2">
              <AlertTriangleIcon className="h-4 w-4" />
              <AlertDescription>
                {kind === 'video' ? t('women.media.videoLinkWarning') : t('women.media.imageLinkWarning')}
              </AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Like MediaSourceField, for a value that has no stored "upload or link" choice. */
export function MediaField(props: Omit<React.ComponentProps<typeof MediaSourceField>, 'mode' | 'onModeChange'>) {
  const [mode, setMode] = useState<MediaSourceMode>('upload');
  return <MediaSourceField {...props} mode={mode} onModeChange={setMode} />;
}

/** Vocabulary pairs shown under a card (English word → Portuguese meaning). */
export function VocabEditor({
  idBase,
  value,
  onChange,
  max,
}: {
  idBase: string;
  value: VocabPair[] | undefined;
  onChange: (value: VocabPair[]) => void;
  max: number;
}) {
  const { t } = useTranslation('admin');
  const pairs = value ?? [];
  const update = (index: number, patch: Partial<VocabPair>) =>
    onChange(pairs.map((pair, i) => (i === index ? { ...pair, ...patch } : pair)));

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('women.cards.fields.vocab')}</Label>
      {pairs.map((pair, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            id={`${idBase}-vocab-${index}-en`}
            className="min-w-0 flex-1"
            value={pair.en}
            placeholder={t('women.cards.fields.vocabEn')}
            aria-label={t('women.cards.fields.vocabEn')}
            onChange={(event) => update(index, { en: event.target.value })}
          />
          <Input
            id={`${idBase}-vocab-${index}-pt`}
            className="min-w-0 flex-1"
            value={pair.pt}
            placeholder={t('women.cards.fields.vocabPt')}
            aria-label={t('women.cards.fields.vocabPt')}
            onChange={(event) => update(index, { pt: event.target.value })}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 p-1"
            aria-label={t('women.cards.fields.vocabRemove')}
            onClick={() => onChange(pairs.filter((_, i) => i !== index))}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pairs.length >= max}
          onClick={() => onChange([...pairs, { en: '', pt: '' }])}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          {t('women.cards.fields.vocabAdd')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('women.cards.fields.vocabHint', { max })}</p>
    </div>
  );
}
